import { z } from 'zod';
import { CONFIG_KINDS, SCOPE_LEVELS } from './config.js';
import { compileFlow } from './flow.js';
import { compileForms } from './insurer-forms.js';
import { applyIndustryProfile } from './profiles.js';
export { canonicalJson } from './json.js';
/**
 * Publishing: approved drafts become an immutable version per author (the platform, or a tenant). An
 * assessment runs on a bundle: the platform's and its tenant's newest versions resolved for the
 * assessment's context into one flat set of items, where the narrowest scope wins.
 *
 * Platform → Country → Industry → Tenant → Product → Customer (most general first).
 */
/** What an assessment is about, for picking the scopes that apply. */
export const BundleContext = z.strictObject({
    /** ISO 3166-1 alpha-2, e.g. MY. */
    country: z
        .string()
        .regex(/^[A-Z]{2}$/, 'a two-letter country code, e.g. MY')
        .nullable()
        .default(null),
    industry: z.string().trim().min(1).max(100).nullable().default(null),
    product: z.string().trim().min(1).max(100).nullable().default(null),
    customer: z.string().trim().min(1).max(100).nullable().default(null),
});
/** Does an item at this scope apply to the context? Tenant-level items apply to every assessment of their tenant. */
function applies(it, ctx, tenantId) {
    switch (it.scope_level) {
        case 'platform':
            return true;
        case 'country':
            return it.scope_key === ctx.country;
        case 'industry':
            return it.scope_key === ctx.industry;
        case 'tenant':
            return it.scope_key === tenantId;
        case 'product':
            return it.scope_key === ctx.product;
        case 'customer':
            return it.scope_key === ctx.customer;
    }
}
/**
 * Resolves the inheritance chain. For each kind and key, the item at the narrowest applicable scope wins.
 * The platform authors platform, country and industry items; the tenant authors tenant, product and
 * customer items, so one tenant's overrides never reach another tenant.
 */
export function resolveBundle(platform, tenant, ctx, tenantId) {
    const rank = (l) => SCOPE_LEVELS.indexOf(l);
    const winners = new Map();
    const consider = (items, from) => {
        for (const it of items) {
            if (!applies(it, ctx, tenantId))
                continue;
            const id = `${it.kind}/${it.key}`;
            const cur = winners.get(id);
            if (!cur || rank(it.scope_level) > rank(cur.it.scope_level))
                winners.set(id, { it, from });
        }
    };
    consider(platform, 'platform');
    consider(tenant, 'tenant');
    const items = Object.fromEntries(CONFIG_KINDS.map((k) => [k, {}]));
    const sources = {};
    for (const id of [...winners.keys()].sort()) {
        const { it, from } = winners.get(id);
        items[it.kind][it.key] = it.body;
        sources[id] = {
            from,
            scope_level: it.scope_level,
            scope_key: it.scope_key,
            item_id: it.item_id,
            revision: it.revision,
        };
    }
    // Stage routes become the skipped questions' `skipped_by` (task 85), so the bundle carries the whole flow;
    // the insurer forms decide which of their questions are asked (task 96); and the industry's profile marks
    // its kill switches and sets the coverage target (task 79).
    return { items: applyIndustryProfile(compileFlow(compileForms(items)), ctx.industry), sources };
}
/** A stable cache and lookup key for a context. */
export function contextKey(ctx) {
    return [ctx.country, ctx.industry, ctx.product, ctx.customer].map((v) => v ?? '-').join('|');
}
// ---------- releases (maker-checker) ----------
export const RELEASE_STATUSES = ['pending', 'approved', 'rejected', 'withdrawn'];
export const ReleaseStatus = z.enum(RELEASE_STATUSES);
export const SubmitRelease = z.strictObject({
    note: z.string().trim().min(1, 'Say what changed and why').max(1000),
});
export const SubmitRollback = z.strictObject({
    /** The earlier version to publish again. */
    version: z.number().int().positive(),
    note: z.string().trim().min(1, 'Say why you are rolling back').max(1000),
});
export const DecideRelease = z.strictObject({ note: z.string().trim().max(1000).optional() });
export const RejectRelease = z.strictObject({
    note: z.string().trim().min(1, 'Say why, so the author can fix it').max(1000),
});
//# sourceMappingURL=publishing.js.map